import {
  buildCatalogs,
  FINDINGS_MANAGE_PERMISSION,
  KERNEL_CATALOGS,
  listedOnSettingsPages,
  moduleSettings,
  modulePath,
  type CatalogSetting,
  type ModuleManifest,
  type ResolvedCatalog,
  type SequenceDeclaration,
} from '@nexus/core';
import type { SessionInfo } from '../api/types';
import { canIn } from '../session/session';

/** Mirrors `SettingsService.canManage` on the server, only to decide what to show. */
export function canManageSetting(session: SessionInfo, setting: CatalogSetting): boolean {
  if (setting.level === 'global') return session.superadmin;
  return canIn(
    session,
    setting.owner === 'core' ? 'company.settings.manage' : `${setting.owner}.settings.manage`,
  );
}

export interface ModuleSettings {
  /** Personal settings shown in the module, `placement: 'module'`. */
  personal: CatalogSetting[];
  /** Admin values the person may change: company and global settings, defaults of personal ones. */
  admin: CatalogSetting[];
}

/** The module and its submodules, whose settings share one page. */
function withSubmodules(module: ModuleManifest, all: readonly ModuleManifest[]): ModuleManifest[] {
  return [module, ...all.filter((m) => m.parent === module.id)];
}

/**
 * Settings of a module and its active submodules for its settings page: submodules get a section
 * on the page of their parent (docs/architecture/modules.md, Einstellungen).
 */
export function settingsOfModule(
  module: ModuleManifest,
  all: readonly ModuleManifest[],
  session: SessionInfo,
): ModuleSettings {
  const active = new Set(session.modules);
  const settings = [module, ...all.filter((m) => m.parent === module.id)]
    .filter((m) => active.has(m.id))
    .flatMap(moduleSettings)
    .filter(listedOnSettingsPages);
  return {
    personal: settings.filter((s) => s.level === 'user' && s.placement !== 'profile'),
    admin: settings.filter((s) => canManageSetting(session, s)),
  };
}

/** Number sequences of a module and its active submodules the person may change. */
export function sequencesOfModule(
  module: ModuleManifest,
  all: readonly ModuleManifest[],
  session: SessionInfo,
): SequenceDeclaration[] {
  const active = new Set(session.modules);
  return [module, ...all.filter((m) => m.parent === module.id)]
    .filter((m) => active.has(m.id) && canIn(session, `${m.id}.settings.manage`))
    .flatMap((m) => m.sequences ?? []);
}

/**
 * Number sequences or something the company maintains on the settings page: catalogs, findings
 * (with their catalogs), document or questionnaire templates.
 */
function declaresCompanyContent(m: ModuleManifest): boolean {
  return (
    (m.sequences ?? []).length +
      (m.catalogs ?? []).length +
      (m.findings ?? []).length +
      (m.documents ?? []).length +
      (m.questionnaires ?? []).length >
    0
  );
}

/** The module or one of its submodules declares settings or something the company maintains. */
export function hasSettingsPage(module: ModuleManifest, all: readonly ModuleManifest[]): boolean {
  return withSubmodules(module, all).some(
    (m) => (m.settings ?? []).length > 0 || declaresCompanyContent(m),
  );
}

export function moduleSettingsPath(moduleId: string): string {
  return `${modulePath(moduleId)}/settings`;
}

/**
 * Catalogs of a module and its submodules, those derived from findings included. The kernel
 * catalogs come from `kernelCatalogsOfModule` (docs/architecture/catalogs.md).
 */
export function catalogsOfModule(
  module: ModuleManifest,
  all: readonly ModuleManifest[],
): ResolvedCatalog[] {
  return buildCatalogs([module, ...all.filter((m) => m.parent === module.id)]).filter(
    (c) => c.owner !== 'core',
  );
}

/** Catalogs of a module and its active submodules the person may maintain. */
export function manageableCatalogs(
  module: ModuleManifest,
  all: readonly ModuleManifest[],
  session: SessionInfo,
): ResolvedCatalog[] {
  const active = new Set(session.modules);
  return catalogsOfModule(module, all).filter(
    (c) => active.has(c.owner) && canIn(session, c.manage),
  );
}

/**
 * Statuses and priorities of findings for the settings page of a module with findings, its own
 * or those of a submodule. All modules with findings share them.
 */
export function kernelCatalogsOfModule(
  module: ModuleManifest,
  all: readonly ModuleManifest[],
): readonly ResolvedCatalog[] {
  return withSubmodules(module, all).some((m) => (m.findings ?? []).length > 0)
    ? KERNEL_CATALOGS
    : [];
}

/**
 * Kernel catalogs on the settings page of the module for the person: with
 * `company.findings.manage`, while the module and a module of it with findings are active.
 */
export function manageableKernelCatalogs(
  module: ModuleManifest,
  all: readonly ModuleManifest[],
  session: SessionInfo,
): readonly ResolvedCatalog[] {
  const active = new Set(session.modules);
  if (!active.has(module.id) || !canIn(session, FINDINGS_MANAGE_PERMISSION)) return [];
  return kernelCatalogsOfModule(
    module,
    all.filter((m) => active.has(m.id)),
  );
}

/**
 * The settings page of an active main module offers the person something to change for the
 * company: admin values, templates, catalogs or number sequences of a module they set up, or the
 * kernel catalogs of findings.
 */
export function managesModuleSettings(
  module: ModuleManifest,
  all: readonly ModuleManifest[],
  session: SessionInfo,
): boolean {
  const active = new Set(session.modules);
  if (!active.has(module.id)) return false;
  return (
    settingsOfModule(module, all, session).admin.length > 0 ||
    withSubmodules(module, all).some(
      (m) =>
        active.has(m.id) && canIn(session, `${m.id}.settings.manage`) && declaresCompanyContent(m),
    ) ||
    manageableKernelCatalogs(module, all, session).length > 0
  );
}

/** Main modules whose settings page offers the person something to change for the company. */
export function managedSettingsPages(
  all: readonly ModuleManifest[],
  session: SessionInfo,
): ModuleManifest[] {
  return all.filter(
    (m) => !m.parent && hasSettingsPage(m, all) && managesModuleSettings(m, all, session),
  );
}

/** Page of a catalog below the settings page of the main module, the kernel catalogs included. */
export function catalogPagePath(moduleId: string, key: string): string {
  return `${moduleSettingsPath(moduleId)}/catalogs/${encodeURIComponent(key)}`;
}
